import React, { useState, useEffect } from 'react';
import { User, Mail, Clock, MapPin, BookOpen, Edit3, Save, X, Key, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface Subject {
  id: string;
  name: string;
  category: string;
}

interface ProfileData {
  name: string;
  email: string;
  timezone: string;
  role: string;
  studentProfile?: {
    description: string;
    subjects: Subject[];
  };
}

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    timezone: '',
    description: '',
    subjects: [] as string[]
  });
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [showEmailSection, setShowEmailSection] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [requestingEmailChange, setRequestingEmailChange] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchAllSubjects();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/profile');
      // The response has a nested user object
      const userData = response.data.user || response.data;
      setProfileData(userData);
      setEditForm({
        name: userData.name,
        email: userData.email,
        timezone: userData.timezone || 'Europe/London',
        description: userData.studentProfile?.description || '',
        subjects: userData.studentProfile?.subjects?.map((s: Subject) => s.id) || []
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setError('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Email is changed through the verified /auth/email-change/request
      // flow, NOT this endpoint — strip it from the payload so a plain
      // profile save can never bypass verification.
      const { email: _ignored, ...profilePayload } = editForm;
      const response = await api.put('/users/profile', profilePayload);

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      await fetchProfile();
      // Refresh user context to ensure changes are reflected website-wide
      await refreshUser();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Profile update error:', error);
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await api.get('/subjects');
      setAllSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleSubjectToggle = (subjectId: string) => {
    setEditForm(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter(id => id !== subjectId)
        : [...prev.subjects, subjectId]
    }));
  };

  const getSubjectsByCategory = (category: string) => {
    return allSubjects.filter(subject => subject.category === category);
  };

  const handleCancel = () => {
    if (profileData) {
      setEditForm({
        name: profileData.name,
        email: profileData.email,
        timezone: profileData.timezone || 'Europe/London',
        description: profileData.studentProfile?.description || '',
        subjects: profileData.studentProfile?.subjects?.map((s: Subject) => s.id) || []
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = async () => {
    try {
      setChangingPassword(true);
      setError('');
      setSuccess('');

      // Validate passwords match
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setError('New passwords do not match');
        return;
      }

      // Validate password length
      if (passwordForm.newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      await api.put('/users/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      });

      setSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow-soft p-8">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">Failed to load profile data.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-2">
            Manage your account information and preferences.
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <Save className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <X className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-white">{profileData.name}</h2>
                <p className="text-primary-100 capitalize">{profileData.role ? profileData.role.toLowerCase() : 'user'}</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {!isEditing ? (
              // View Mode
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-outline flex items-center"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Full Name</p>
                        <p className="text-gray-900">{profileData.name}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email Address</p>
                        <p className="text-gray-900">{profileData.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Timezone</p>
                        <p className="text-gray-900">{profileData.timezone || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  {profileData.studentProfile && (
                    <div>
                      <div className="flex items-start">
                        <BookOpen className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-500 mb-2">About Me</p>
                          <p className="text-gray-900">
                            {profileData.studentProfile.description || 'No description provided.'}
                          </p>
                        </div>
                      </div>

                      {profileData?.studentProfile?.subjects && Array.isArray(profileData.studentProfile.subjects) && profileData.studentProfile.subjects.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm font-medium text-gray-500 mb-2">Enrolled Subjects</p>
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(profileData.studentProfile.subjects) ? profileData.studentProfile.subjects.map((subject) => (
                              <span
                                key={subject.id}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                              >
                                {subject.name}
                              </span>
                            )) : null}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Edit Mode
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Profile</h3>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleCancel}
                      className="btn btn-secondary flex items-center"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn btn-primary flex items-center"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="input w-full"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        className="input w-full bg-gray-50 cursor-not-allowed"
                        readOnly
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use the Change Email section below to update your email — we'll send a confirmation link.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        value={editForm.timezone}
                        onChange={(e) => setEditForm(prev => ({ ...prev, timezone: e.target.value }))}
                        className="input w-full"
                      >
                        <option value="Europe/London">London (GMT)</option>
                        <option value="America/New_York">New York (EST)</option>
                        <option value="America/Los_Angeles">Los Angeles (PST)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                      </select>
                    </div>
                  </div>

                  {profileData.studentProfile && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          About Me
                        </label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                          rows={4}
                          className="input w-full"
                          placeholder="Tell us a bit about yourself, your learning goals, or interests..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Subject Preferences
                        </label>
                        {loadingSubjects ? (
                          <div className="text-sm text-gray-500">Loading subjects...</div>
                        ) : (
                          <div className="space-y-4">
                            {['ELEVEN_PLUS', 'GCSE', 'A_LEVEL', 'UNIVERSITY'].map(category => {
                              const categorySubjects = getSubjectsByCategory(category);
                              if (categorySubjects.length === 0) return null;

                              const label =
                                category === 'ELEVEN_PLUS' ? '11+' :
                                category === 'GCSE' ? 'GCSE' :
                                category === 'A_LEVEL' ? 'A-Level' :
                                category === 'UNIVERSITY' ? 'University / Oxbridge' :
                                category.replace('_', ' ');

                              return (
                                <div key={category} className="space-y-2">
                                  <h4 className="text-sm font-medium text-gray-600">{label}</h4>
                                  <div className="grid grid-cols-2 gap-2">
                                    {categorySubjects.map(subject => (
                                      <label key={subject.id} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={editForm.subjects.includes(subject.id)}
                                          onChange={() => handleSubjectToggle(subject.id)}
                                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-gray-700">{subject.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Select the subjects you're interested in learning. This helps us personalize your experience.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Change Section */}
        <div className="bg-white rounded-lg shadow-soft overflow-hidden mt-6">
          <div className="px-8 py-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-gray-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Change Email</h3>
              </div>
              <button
                onClick={() => {
                  setShowEmailSection(!showEmailSection);
                  if (showEmailSection) {
                    setEmailForm({ newEmail: '', currentPassword: '' });
                    setError('');
                    setSuccess('');
                  }
                }}
                className="btn btn-outline flex items-center"
              >
                {showEmailSection ? (
                  <><X className="w-4 h-4 mr-2" />Cancel</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />Change Email</>
                )}
              </button>
            </div>
          </div>
          {showEmailSection && (
            <div className="px-8 py-6">
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New email address
                  </label>
                  <input
                    type="email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                    className="input w-full"
                    required
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current password
                  </label>
                  <div className="relative">
                    <input
                      type={showEmailPassword ? 'text' : 'password'}
                      value={emailForm.currentPassword}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="input w-full pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmailPassword(!showEmailPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showEmailPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  We'll send a confirmation link to the new address. Your email won't change until you click it.
                </p>
                <button
                  onClick={async () => {
                    setError('');
                    setSuccess('');
                    if (!emailForm.newEmail || !emailForm.currentPassword) {
                      setError('Please fill both fields.');
                      return;
                    }
                    setRequestingEmailChange(true);
                    try {
                      await api.post('/auth/email-change/request', emailForm);
                      setSuccess(`Check ${emailForm.newEmail} for the confirmation link. You can close this section.`);
                      setEmailForm({ newEmail: '', currentPassword: '' });
                      setShowEmailSection(false);
                      setTimeout(() => setSuccess(''), 8000);
                    } catch (e: any) {
                      setError(e?.response?.data?.message || 'Could not send confirmation email.');
                    } finally {
                      setRequestingEmailChange(false);
                    }
                  }}
                  disabled={requestingEmailChange}
                  className="btn btn-primary"
                >
                  {requestingEmailChange ? 'Sending…' : 'Send confirmation link'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Password Change Section */}
        <div className="bg-white rounded-lg shadow-soft overflow-hidden mt-6">
          <div className="px-8 py-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Key className="w-5 h-5 text-gray-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
              </div>
              <button
                onClick={() => {
                  setShowPasswordSection(!showPasswordSection);
                  if (showPasswordSection) {
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setError('');
                    setSuccess('');
                  }
                }}
                className="btn btn-outline flex items-center"
              >
                {showPasswordSection ? (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </button>
            </div>
          </div>

          {showPasswordSection && (
            <div className="px-8 py-6">
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="input w-full pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="input w-full pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 6 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="input w-full pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    onClick={handlePasswordChange}
                    disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;